<!-- BaseTinyEditor.vue (common) -->
<template tag="공통 에디터 컴포넌트">
  <div>
    <Editor
      ref="editorRef"
      v-if="isEditorReady"
      :key="`admin-${editorKey}`"
      v-model="model"
      :init="adminInitOptions"
      :readonly="readonly"
      :tinymceScriptSrc="tinymceScript"
    />
    <!-- 로딩 상태 -->
    <div v-else class="editor-loading">loading...</div>

    <!-- 글자수 / 에러 한 줄 표시 (grid: 에러=좌 1fr / 글자수=우 auto) -->
    <div
      v-if="(enableLimit && maxLength > 0) || (error && errorMessage)"
      class="mt-2"
      style="
        display: grid;
        grid-template-columns: 1fr auto;
        align-items: center;
        gap: 8px;
        min-height: 20px;
        padding-top: 10px;
      "
    >
      <p v-if="error && errorMessage" class="lms_field_error ma-0" style="grid-column: 1">
        <i class="icon_triangle_alert"></i>{{ errorMessage }}
      </p>
      <div
        v-if="enableLimit && maxLength > 0"
        style="grid-column: 2; justify-self: end; font-size: 13px; color: #666; white-space: nowrap"
      >
        <span class="mx-1" :class="textLength > maxLength ? 'text-error' : ''">{{
          textLength
        }}</span>
        <span>/</span>
        <span>{{ maxLength }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { global, i18n } from "@/app/plugins/global";

import {
  ref,
  onBeforeMount,
  onMounted,
  onBeforeUnmount,
  nextTick,
  onActivated,
  onDeactivated,
} from "vue";
import Editor from "@tinymce/tinymce-vue";
import { type Editor as EditorType } from "tinymce";
import { getBase64ToFile } from "@/api/cmmn/FileApi";
import "mathlive";
import { convertLatexToMarkup, type MathfieldElement } from "mathlive";
// MathLive 정적 렌더 스타일 (TinyMCE iframe 본문에 주입)
import mathStaticCss from "mathlive/static.css?inline";

// dynamically resolve base path
const basePath = import.meta.env.BASE_URL;
const tinymceScript = basePath + "tinymce/tinymce.min.js";

/* ------------------------------------------------------------
                          Vue Instance & Global Properties
   ------------------------------------------------------------ */
const { $text } = i18n();
const vm = global();

/* ------------------------------------------------------------
                          Model
   ------------------------------------------------------------ */
/** admin 모드용  */
const model = defineModel<string>();

/** 에디터 초기화(콘텐츠 렌더) 완료 신호 — 부모가 전체 로딩 오버레이 해제 등에 사용 (opt-in) */
const emit = defineEmits<{ ready: [] }>();

/* ------------------------------------------------------------
                          Props
   ------------------------------------------------------------ */
const props = defineProps({
  id: { type: String, default: "" },
  readonly: { type: Boolean, default: false }, // admin 모드용
  disabled: { type: Boolean, default: false }, // 일반 모드용
  maxLength: { type: Number, default: 3000 },
  enableLimit: { type: Boolean, default: true },
  // 에디터에서 이미지 업로드 시 파일이 저장될 테이블 이름
  // 지정하지 않으면 "files"로 업로드됨
  imageTableName: { type: String, default: "files" },
  // 수식(LaTeX) 입력 기능 활성화
  enableMath: { type: Boolean, default: false },
  // 확장 모드 — 시험 문항 전용. 밑줄·취소선·글자크기(12~32)·인용·이미지정렬·수식 추가
  extended: { type: Boolean, default: false },
  placeholder: { type: String, default: "" },
  error: { type: Boolean, default: false },
  errorMessage: { type: String, default: "" },
});

/* ------------------------------------------------------------
                          Variable Declarations
   ------------------------------------------------------------ */
const editorRef = ref();
const editorInstance = ref<EditorType | null>(null);
const textLength = ref(0);
const isEditorReady = ref(false);
const editorKey = ref(Date.now()); // 고유 키 생성
const storedLang = sessionStorage.getItem("lang");
const imageMimeTypes = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/bmp",
  "image/webp",
];

const readAsDataUrl = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("file read failed"));
    reader.readAsDataURL(blob);
  });
};

const MATH_FIELD_ID = "tinymce-math-field";

const renderMathHtml = (latex: string): string => {
  if (!latex.trim()) return "";
  try {
    const markup = convertLatexToMarkup(latex);
    const safeLatex = encodeURIComponent(latex);
    return `<span class="math-tex" data-latex="${safeLatex}" contenteditable="false">${markup}</span>`;
  } catch (e) {
    console.error("LaTeX 변환 실패:", e);
    return "";
  }
};

const openMathDialog = (editor: EditorType, initialLatex = "") => {
  editor.windowManager.open({
    title: "수식 입력",
    size: "medium",
    body: {
      type: "panel",
      items: [
        {
          type: "htmlpanel",
          html: `
            <div class="tinymce-math-dialog">
              <math-field id="${MATH_FIELD_ID}"
                          math-virtual-keyboard-policy="manual"
                          style="width:100%;min-height:80px;font-size:20px;border:1px solid #ddd;padding:10px;border-radius:4px;background:#fff;display:block;">${initialLatex}</math-field>
              <div class="tinymce-math-hint" style="margin-top:6px;font-size:12px;color:#666;">
                LaTeX 직접 입력하거나, 우측 하단 키보드 아이콘을 눌러 가상키보드로 입력하세요.
              </div>
            </div>
          `,
        },
      ],
    },
    buttons: [
      { type: "cancel", text: "취소" },
      { type: "submit", text: "삽입", buttonType: "primary" },
    ],
    onSubmit: (api) => {
      const mf = document.getElementById(MATH_FIELD_ID) as MathfieldElement | null;
      const latex = (mf?.value ?? "").trim();
      if (latex) {
        editor.insertContent(renderMathHtml(latex) + "&nbsp;");
      }
      api.close();
    },
    onClose: () => {
      // 다이얼로그가 어떤 방식으로든 닫히면 가상키보드도 함께 숨김
      window.mathVirtualKeyboard?.hide();
    },
  });

  // 다이얼로그 렌더링 완료 후 입력 필드에 포커스
  setTimeout(() => {
    const mf = document.getElementById(MATH_FIELD_ID) as MathfieldElement | null;
    mf?.focus();
  }, 50);
};

const uploadBase64Image = async (base64: string, context: string): Promise<string | null> => {
  try {
    const filePath = await getBase64ToFile(base64, props.imageTableName);
    if (!filePath) {
      vm.$toast($text("editor.imageUploadFail"), "error");
      return null;
    }
    return filePath;
  } catch (error) {
    console.error(`이미지 업로드 중 오류(${context}):`, error);
    vm.$toast($text("editor.imageUploadFail"), "error");
    return null;
  }
};

// Admin 모드 TinyMCE 옵션
const adminInitOptions = {
  base_url: basePath + "tinymce", // self-hosted base
  min_height: 400,
  max_height: 600,
  resize: false,
  menubar: "format insert tools table",
  statusbar: false,
  branding: false,
  promotion: false,
  placeholder: props.placeholder,
  // 공통: lists link code table media. 확장(extended): + quickbars (이미지 정렬)
  plugins: props.extended
    ? "lists link autolink code table media quickbars autoresize"
    : "lists link autolink code table media autoresize",
  link_default_target: "_blank",
  // 공통 toolbar / 확장 toolbar (밑줄·취소선·글자크기·인용·이미지정렬·수식 추가) 분기 — 시험 문항 전용
  toolbar: props.extended
    ? "undo redo | formatselect fontsize | bold italic underline strikethrough | forecolor backcolor |" +
      "alignleft aligncenter alignright alignjustify | " +
      "bullist numlist outdent indent | blockquote | customimage link table mathformula | removeformat | code media"
    : "undo redo | formatselect | bold italic | forecolor backcolor |" +
      "alignleft aligncenter alignright alignjustify | " +
      "bullist numlist outdent indent | customimage link table | removeformat | code media" +
      (props.enableMath ? " | math" : ""),
  // extended 전용 — SB 폰트 크기 5단계(12/14/18/24/32) + 이미지 리사이즈/정렬 quickbar
  ...(props.extended
    ? {
        fontsize_formats: "12px 14px 18px 24px 32px",
        object_resizing: true,
        image_dimensions: true,
        quickbars_selection_toolbar: false,
        quickbars_insert_toolbar: false,
        quickbars_image_toolbar: "alignleft aligncenter alignright",
      }
    : {}),
  elementpath: false,
  // 빈 줄(엔터 두 번)을 &nbsp; 대신 <br> 로 채움 — EUC-KR/MS949 로 저장하는 운영 서버에서
  // &nbsp;(U+00A0)가 매핑되지 못해 전각 물음표(？)로 깨지는 문제 예방
  padd_empty_with_br: true,
  paste_data_images: true,
  paste_webkit_styles: "all",
  paste_merge_formats: true,
  automatic_uploads: true,
  relative_urls: false,
  images_upload_handler: (blobInfo: { blob: () => Blob }) => {
    return new Promise((resolve, reject) => {
      readAsDataUrl(blobInfo.blob())
        .then((base64) => uploadBase64Image(base64, "auto"))
        .then((filePath) => {
          if (!filePath) {
            reject(new Error("image upload failed"));
            return;
          }
          resolve(filePath);
        })
        .catch((error) => reject(error));
    });
  },
  // valid_elements: "*[*]",
  content_css: basePath + "tinymce/tiny-editor-content.css",
  content_style: `
    ${mathStaticCss}

    html,
    body {
      min-height: 100%;
    }

    body.mce-content-body {
      margin: 0 !important;
      padding: 24px 16px !important;
      box-sizing: border-box;
      font-family: "Pretendard", sans-serif;
      font-size: 16px;
      line-height: 1.6;
      color: rgba(0, 0, 0, 0.8);
    }

    body.mce-content-body > *:first-child {
      margin-top: 0;
    }

    body.mce-content-body[data-mce-placeholder]::before {
      top: 24px !important;
      left: 32px !important;
      color: rgba(0, 0, 0, 0.35) !important;
      font-size: 16px;
      font-weight: 400;
      line-height: 24px;
      pointer-events: none;
    }

    body.mce-content-body:focus[data-mce-placeholder]::before {
      display: none !important;
    }

    /* 표 삽입/붙여넣기 시 기본 테두리 표시 — .tbl 계열 커스텀 템플릿 표는 자체 !important 규칙이 우선 적용됨 */
    body.mce-content-body table {
      border-collapse: collapse;
    }
    body.mce-content-body table td,
    body.mce-content-body table th {
      border: 1px solid #ccc;
    }
  `,
  language: sessionStorage.getItem("lang"),
  language_url: `/tinymce/lang/${storedLang}.js`,
  //the Tiny Community Language Packages download page: https://www.tiny.cloud/get-tiny/language-packages/
  setup: function (editor: EditorType) {
    // 에러 처리 추가
    editor.on("error", (e: unknown) => {
      console.error("TinyMCE 런타임 에러:", e);
    });

    // 표 삽입/행·열 추가 시 각 셀에 인라인 border 를 직접 부여.
    // 기본적으로 TinyMCE 는 <table> 태그에만 border="1" 속성을 붙이고 td/th 는 스타일이 전혀 없어서,
    // 편집기 안에서도(별도 CSS 없이는) 셀 테두리가 보이지 않고, 저장 후 다른 페이지에 렌더링될 때도
    // 마찬가지로 테두리가 사라진다. 셀 자체에 인라인 스타일을 심어 어디서 렌더링되든 보이게 한다.
    editor.on("NewCell", (e: { node: HTMLTableCellElement }) => {
      if (!e.node.style.border) {
        e.node.style.border = "1px solid #ccc";
      }
    });

    editor.on("keydown", function (e: KeyboardEvent) {
      // maxLength <= 0 이면 무제한 (글자수 제한 없음)
      if (
        props.maxLength > 0 &&
        textLength.value >= props.maxLength &&
        e.keyCode !== 8 &&
        e.keyCode !== 46
      ) {
        e.preventDefault();
        vm.$toast($text("editor.maxLength"), "error");
      }
    });

    editor.on("input keyup setcontent", function () {
      const textOnly = editor.getContent({ format: "text" }).trim();
      textLength.value = textOnly.length;
    });

    // 붙여넣기 등으로 들어온, 내용이 nbsp 뿐인 빈 블록(예: <p>nbsp</p>)도 padd_empty_with_br
    // 와 동일하게 <br> 로 정규화 - EUC-KR/MS949 로 저장하는 운영 서버에서 nbsp(U+00A0)가
    // 매핑되지 못해 전각 물음표로 깨져 저장되는 문제 방지.
    editor.on("GetContent", function (e: { content: string; format?: string }) {
      if (e.format === "text") return;
      e.content = e.content.replace(
        /<(\w+)([^>]*)>(?:&nbsp;|\u00a0)+<\/\1>/gi,
        "<$1$2><br /></$1>"
      );
      // Remaining nbsp (entity or raw char) after real text, e.g. from "word" + space + Enter,
      // is normalized to a plain space. Leaving it as the "&nbsp;" entity is not enough: any
      // later HTML re-parse/serialize (backend sanitizer, re-editing, etc.) turns it back into
      // raw U+00A0, which breaks when encoded as EUC-KR/MS949 on the production server.
      e.content = e.content.replace(/&nbsp;|\u00a0/gi, " ");

      // Bake table borders in as inline styles, regardless of how the table was created
      // (typed via the toolbar grid, or pasted from Word/Excel), so the border still shows
      // when this content is later rendered on another page without the editor's CSS.
      // Custom template tables (.tbl and friends) keep their own design and are skipped.
      if (/<table/i.test(e.content)) {
        const wrapper = document.createElement("div");
        wrapper.innerHTML = e.content;
        wrapper.querySelectorAll("table").forEach((table) => {
          if (table.closest(".tbl, .tbl_row, .tbl_col, .tbl_secondary")) return;
          if (!table.style.borderCollapse) table.style.borderCollapse = "collapse";
          table.querySelectorAll("td, th").forEach((cell) => {
            const cellEl = cell as HTMLElement;
            if (!cellEl.style.border) cellEl.style.border = "1px solid #ccc";
          });
        });
        e.content = wrapper.innerHTML;
      }
    });

    editor.on("paste", function (e: ClipboardEvent) {
      if (props.maxLength <= 0) return; // 무제한 — 붙여넣기 자르기 안 함
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const clipboardData = e.clipboardData || (window as any).clipboardData;
      if (!clipboardData) return;

      // 일반 텍스트 붙여넣기 시 글자 수 제한
      const pastedText = clipboardData.getData("text/plain");
      const allowedLength = Math.max(props.maxLength - textLength.value, 0);

      if (pastedText.length > allowedLength) {
        e.preventDefault();
        if (allowedLength > 0) {
          const trimmedText = pastedText.slice(0, allowedLength);
          editor.insertContent(trimmedText);
        }
        vm.$toast($text("editor.maxLength"), "error");
      }
    });
    editorInstance.value = editor;

    // 에디터 init(콘텐츠 첫 렌더) 완료 시점 — 부모에 알림
    editor.on("init", () => emit("ready"));

    if (props.enableMath) {
      editor.ui.registry.addButton("math", {
        text: "수식",
        tooltip: "수식 입력",
        onAction: () => openMathDialog(editor),
      });
    }

    // 확장 모드 — 시험 문항 전용 수식 버튼 (toolbar 의 mathformula)
    if (props.extended) {
      editor.ui.registry.addButton("mathformula", {
        text: "수식",
        tooltip: "수식 입력",
        onAction: () => openMathDialog(editor),
      });
    }

    editor.ui.registry.addButton("customimage", {
      icon: "image",
      tooltip: $text("editor.imageTooltip"),
      onAction: () => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = "image/jpeg,image/jpg,image/png,image/gif,image/bmp,image/webp";

        input.onchange = (e: Event) => {
          const target = e.target as HTMLInputElement;
          const files = target.files;
          if (files && files.length > 0) {
            const file = files[0];
            // 파일 타입 검증
            if (!imageMimeTypes.includes(file.type)) {
              vm.$toast($text("editor.noImage"), "error");
              return;
            }

            readAsDataUrl(file)
              .then((base64) => uploadBase64Image(base64, "button"))
              .then((filePath) => {
                if (!filePath) return;
                editor.insertContent(`<img src="${filePath}" alt="${file.name}" />`);
              });
          }
        };

        input.click();
      },
    });
  },

  // media_url_resolver 옵션 추가
  media_url_resolver: () => Promise.resolve({ html: "" }),
};

/* ------------------------------------------------------------
                                  Methods
   ------------------------------------------------------------ */
/** 에디터 정리 함수 */
const cleanupEditor = () => {
  // ==================================================================
  // 전역 에디터 제거 시 다른 인스턴스의 에디터를 제거할 수도 있어 아래 로직으로 개선
  // ==================================================================
  isEditorReady.value = false;

  const editor = editorRef.value?.editor;
  if (!editor) return;

  try {
    // TinyMCE 7: removed, 일부 버전: destroyed - 둘 다 방어
    const isGone =
      (editor as unknown as { removed?: boolean }).removed === true ||
      (editor as unknown as { destroyed?: boolean }).destroyed === true;

    if (!isGone) {
      editor.destroy();
    }
  } catch (error) {
    console.warn("에디터 정리 중 경고:", error);
  } finally {
    editorInstance.value = null;
  }

  // isEditorReady.value = false;
  // if (editorInstance.value) {
  //   try {
  //     if (!editorInstance.value.destroyed) {
  //       editorInstance.value.destroy();
  //     }
  //   } catch (error) {
  //     console.warn("에디터 정리 중 경고:", error);
  //   } finally {
  //     editorInstance.value = null;
  //   }
  // }

  // // TinyMCE 전역 인스턴스 정리
  // try {
  //   if (window.$tinymce && window.$tinymce.editors) {
  //     const editors = window.$tinymce.editors.slice(); // 복사본 생성
  //     editors.forEach((editor: EditorType) => {
  //       try {
  //         if (editor && !editor.destroyed) {
  //           editor.destroy();
  //         }
  //       } catch (e) {
  //         console.warn("전역 에디터 정리 중 경고:", e);
  //       }
  //     });
  //   }
  // } catch (error) {
  //   console.warn("TinyMCE 전역 정리 중 경고:", error);
  // }
};

/* ------------------------------------------------------------
                          Lifecycle
   ------------------------------------------------------------ */
/** 페이지 렌더링 시 초기값 설정 */
onMounted(async () => {
  // DOM 안정화 후 에디터 활성화
  await nextTick();
  setTimeout(() => {
    isEditorReady.value = true;
  }, 100);
});

/** Admin 모드에서만 실행 */
onBeforeMount(() => {
  (function () {
    const originalAddEventListener = EventTarget.prototype.addEventListener;
    EventTarget.prototype.addEventListener = function (type, listener, options) {
      if (["touchstart", "touchmove"].includes(type)) {
        if (typeof options === "boolean") {
          options = { capture: options, passive: true };
        } else if (typeof options === "object") {
          options = { ...options, passive: true };
        } else {
          options = { passive: true };
        }
      }
      return originalAddEventListener.call(this, type, listener, options);
    };
  })();
});

/** 컴포넌트 언마운트 시 정리 */
onBeforeUnmount(() => {
  cleanupEditor();
});

/** keep-alive 사용 시 처리 */
onDeactivated(() => {
  cleanupEditor();
});

/** keep-alive 사용 시 처리 */
onActivated(async () => {
  await nextTick();
  setTimeout(() => {
    editorKey.value = Date.now(); // 새로운 키 생성
    isEditorReady.value = true;
  }, 100);
});

/* ------------------------------------------------------------
                          Expose
   ------------------------------------------------------------ */
/** 외부에서 사용할 수 있도록 노출 */
defineExpose({
  getEditor: () => editorRef.value?.editor,
});
</script>
